'use client';

/**
 * Login form client logic:
 *   - Zod-validated email + password (min 8 to match Better Auth minPasswordLength)
 *   - POSTs to /api/auth/sign-in/email; Better Auth sets the session cookie
 *   - ?redirect=<path> query param: after success, route there (same-origin paths only)
 *   - ?reset=1 query param: show a "password updated" toast (from /reset-password flow)
 *   - Disables the submit button while in-flight to prevent double-submit
 *   - Additional auth-provider buttons (Google, Apple, magic-link, …) are
 *     driven off the `providers` prop — server's page.tsx reads the
 *     integrations table for category='auth' AND enabled=true AND env vars
 *     present, and hands down the resulting list. Buttons render when the
 *     provider is both admin-enabled AND runtime-configured. Click goes
 *     directly to Better Auth's social sign-in endpoint.
 *
 * Error mapping is intentionally vague ("Check your email and password") to
 * avoid account enumeration. Rate-limit is Better Auth's job on the server.
 */

import { useEffect, useMemo, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { z } from 'zod';

export type LoginProvider = {
  id: string;          // 'auth.email_password' | 'auth.google' | ...
  displayName: string;
  displayOrder: number;
};

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Password is at least 8 characters'),
});

type FormErrors = Partial<Record<'email' | 'password' | 'form', string>>;

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="4" y="10" width="16" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 018 0v3" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 010-4.2V7.07H2.18a11 11 0 000 9.87l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.1 14.97 1 12 1a11 11 0 00-9.82 6.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

export function LoginForm({ providers }: { providers: LoginProvider[] }) {
  const router = useRouter();
  const sp = useSearchParams();
  const redirectTo = safeRedirect(sp.get('redirect'));
  const showResetToast = sp.get('reset') === '1';
  const additionalProviders = providers.filter((p) => p.id !== 'auth.email_password');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const buttonLabel = useMemo(() => (busy ? 'Signing in…' : 'Login Now'), [busy]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});

    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      const fieldErrors: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === 'email' || key === 'password') fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setBusy(true);
    try {
      const res = await fetch('/api/auth/sign-in/email', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: parsed.data.email, password: parsed.data.password, rememberMe: remember }),
      });

      if (res.ok) {
        router.push(redirectTo);
        return;
      }

      // Member Sync PR-A — Better Auth's databaseHooks.session.create.before
      // throws FORBIDDEN with body { message: 'inactive_account' } when the
      // user is suspended or soft-deleted. Surface that as a route to
      // /login?reason=inactive so the existing neutral-copy banner renders
      // (suspend ↔ delete intentionally indistinguishable from the URL).
      if (res.status === 403) {
        let body: { message?: string } | null = null;
        try { body = (await res.clone().json()) as { message?: string }; } catch { /* ignore */ }
        if (body?.message === 'inactive_account') {
          router.push('/login?reason=inactive');
          return;
        }
      }

      if (res.status === 429) {
        setErrors({ form: 'Too many attempts — wait a minute and try again.' });
      } else if (res.status === 401 || res.status === 400) {
        setErrors({ form: 'Check your email and password.' });
      } else {
        setErrors({ form: `Sign-in failed (${res.status}). Try again in a moment.` });
      }
    } catch (err) {
      setErrors({ form: (err as Error).message || 'Network error — try again.' });
    } finally {
      setBusy(false);
    }
  }

  function onSocialClick(providerId: string) {
    // Map integrations-row id → Better Auth social provider name.
    // 'auth.google' → 'google', 'auth.apple' → 'apple', 'auth.magic_link'
    // is special-cased (Better Auth handles magic-link via /sign-in/magic-link,
    // not as a social provider). Fall back to a toast for anything we don't
    // know how to route yet.
    const betterAuthName = providerId.startsWith('auth.')
      ? providerId.slice('auth.'.length)
      : providerId;

    if (betterAuthName === 'magic_link') {
      // Magic-link needs the email input before we can POST; toast for now,
      // wire the real form when we add magic-link (requires email provider).
      setToast('Magic link sign-in: enter your email above, then press Login.');
      return;
    }

    const params = new URLSearchParams({
      provider: betterAuthName,
      callbackURL: redirectTo,
    });
    window.location.href = `/api/auth/sign-in/social?${params.toString()}`;
  }

  return (
    <>
      {showResetToast && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs px-3 py-2">
          Password updated. Sign in with your new password.
        </div>
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2"
        >
          {toast}
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label
            htmlFor="email"
            className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1"
          >
            Email
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <MailIcon />
            </span>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-200 focus:border-[#ff9100] focus:ring-1 focus:ring-[#ff9100] outline-none pl-9 pr-3 py-2.5 text-sm text-[#0f2534]"
            />
          </div>
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1"
          >
            Password
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <LockIcon />
            </span>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-200 focus:border-[#ff9100] focus:ring-1 focus:ring-[#ff9100] outline-none pl-9 pr-3 py-2.5 text-sm text-[#0f2534]"
            />
          </div>
          {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
        </div>

        <div className="flex items-center justify-between text-xs">
          <label className="inline-flex items-center gap-2 text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-[#ff9100] focus:ring-[#ff9100]"
            />
            Remember me
          </label>
          <Link href="/forgot-password" className="text-[#ff9100] font-semibold hover:underline">
            Forgot password?
          </Link>
        </div>

        {errors.form && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-xs px-3 py-2">
            {errors.form}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          style={{ letterSpacing: '0.35px' }}
          className="w-full bg-cta-gradient disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl text-sm transition-transform hover:scale-[1.01]"
        >
          {buttonLabel}
        </button>
      </form>

      {additionalProviders.length > 0 && (
        <>
          <div className="relative my-5 flex items-center">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400">
              or continue with
            </span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <div className="space-y-2" data-additional-providers={additionalProviders.length}>
            {additionalProviders.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onSocialClick(p.id)}
                data-provider={p.id}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
              >
                {p.id === 'auth.google' && <GoogleIcon />}
                Continue with {p.displayName}
              </button>
            ))}
          </div>
        </>
      )}

    </>
  );
}

/**
 * Only allow same-origin relative paths as redirect targets. Anything that
 * doesn't start with "/" or that starts with "//" (protocol-relative) falls
 * back to /dashboard — prevents open-redirect attacks.
 */
function safeRedirect(raw: string | null): string {
  if (!raw) return '/dashboard';
  if (!raw.startsWith('/')) return '/dashboard';
  if (raw.startsWith('//')) return '/dashboard';
  return raw;
}
